(function () {
  const getSwiper = function () {
    if (typeof MinimogLibs !== 'undefined' && MinimogLibs.Swiper) return MinimogLibs.Swiper;
    if (typeof window.Swiper !== 'undefined') return window.Swiper;
    return null;
  };

  const parseBool = function (value, defaultValue) {
    if (value === undefined || value === '') return defaultValue;
    return value === 'true' || value === '1';
  };

  const parseNumber = function (value, defaultValue) {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : defaultValue;
  };

  if (customElements.get('m-slideshow')) return;

  class MSlideshow extends HTMLElement {
    connectedCallback() {
      this.autoplay = parseBool(this.dataset.autoplay, false);
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 5;
      this.showArrows = parseBool(this.dataset.enableArrows, false);
      this.showDots = parseBool(this.dataset.enableDots, false);
      this.adaptHeight = this.dataset.slideHeight === 'adapt';
      this.sectionId = this.dataset.id;

      this.shortSwipes = parseBool(this.dataset.shortSwipes, true);
      this.longSwipes = parseBool(this.dataset.longSwipes, true);
      this.touchThreshold = parseNumber(this.dataset.touchThreshold, 10);
      this.longSwipesRatio = parseNumber(this.dataset.longSwipesRatio, 0.5);
      this.longSwipesMs = parseNumber(this.dataset.longSwipesMs, 300);
      this.touchRatio = parseNumber(this.dataset.touchRatio, 1);

      this.sliderContainer = this.querySelector('.swiper-container');
      this.lastVideo = null;
      this.lastActive = null;
      this.slider = null;
      this._swiperWaitAttempts = 0;
      this._maxSwiperWaitAttempts = 80;

      this._onBlockSelect = this._onBlockSelect.bind(this);
      this._onSectionLoad = this._onSectionLoad.bind(this);
      this._handleChange = this._handleChange.bind(this);

      document.addEventListener('shopify:block:select', this._onBlockSelect);
      document.addEventListener('shopify:section:load', this._onSectionLoad);

      this.initSlider();
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
      document.removeEventListener('shopify:section:load', this._onSectionLoad);
      this.destroySlider();
    }

    _onSectionLoad(event) {
      if (!event.detail || String(event.detail.sectionId) !== String(this.sectionId)) return;
      this.destroySlider();
      this.sliderContainer = this.querySelector('.swiper-container');
      this.initSlider();
    }

    _clearTextSelection() {
      const selection = window.getSelection && window.getSelection();
      if (selection && selection.rangeCount > 0) selection.removeAllRanges();
    }

    _setSwipingActive(active) {
      const section = this.closest('.m-slideshow-section');
      if (section) section.classList.toggle('is-swiping', active);
      this.classList.toggle('is-swiping', active);
    }

    destroySlider() {
      this._setSwipingActive(false);
      if (this.slider) {
        this.slider.destroy(true, true);
        this.slider = null;
      }
      this.lastVideo = null;
    }

    initSlider() {
      const SwiperCtor = getSwiper();
      if (!SwiperCtor || !this.sliderContainer) {
        if (this._swiperWaitAttempts < this._maxSwiperWaitAttempts) {
          this._swiperWaitAttempts += 1;
          window.setTimeout(() => this.initSlider(), 50);
        } else if (!this._swiperInitFailed) {
          this._swiperInitFailed = true;
          console.warn('[Slideshow] Swiper failed to initialize — navigation may not work.');
        }
        return;
      }

      this.destroySlider();

      const slides = this.sliderContainer.querySelectorAll('.swiper-slide');
      const slideCount = slides.length;
      if (slideCount < 1) return;

      const prevEl = this.querySelector('.m-slider-controls__button-prev');
      const nextEl = this.querySelector('.m-slider-controls__button-next');
      const paginationEl = this.querySelector('.swiper-pagination');
      const enableLoop = slideCount > 1;

      this.slider = new SwiperCtor(this.sliderContainer, {
        init: false,
        autoplay:
          this.autoplay && enableLoop
            ? {
                delay: this.autoplaySpeed * 1000,
                disableOnInteraction: true,
              }
            : false,
        slidesPerView: 1,
        slidesPerGroup: 1,
        loop: enableLoop,
        effect: 'fade',
        fadeEffect: { crossFade: true },
        speed: 1000,
        watchOverflow: true,
        shortSwipes: this.shortSwipes,
        longSwipes: this.longSwipes,
        longSwipesRatio: this.longSwipesRatio,
        longSwipesMs: this.longSwipesMs,
        threshold: this.touchThreshold,
        touchRatio: this.touchRatio,
        touchStartPreventDefault: false,
        preventClicks: true,
        preventClicksPropagation: true,
        noSwiping: true,
        noSwipingClass: 'swiper-no-swiping',
        noSwipingSelector: 'button, .swiper-no-swiping, .m-slider-controls',
        navigation:
          this.showArrows && prevEl && nextEl
            ? {
                nextEl,
                prevEl,
              }
            : false,
        pagination:
          this.showDots && paginationEl
            ? {
                el: paginationEl,
                clickable: true,
                bulletClass: 'm-dot',
                bulletActiveClass: 'm-dot--active',
              }
            : false,
        breakpoints: {
          992: {
            threshold: Math.max(this.touchThreshold, 2),
          },
        },
      });

      this.slider.on('init', this._handleChange);
      this.slider.on('slideChange', this._handleChange);
      this.slider.on('sliderMove', () => {
        this._setSwipingActive(true);
        this._clearTextSelection();
      });
      this.slider.on('touchEnd', () => this._setSwipingActive(false));
      this.slider.on('transitionEnd', () => this._setSwipingActive(false));
      this.slider.init();
    }

    _handleChange(swiper) {
      const { slides, activeIndex } = swiper;
      const activeSlide = slides[activeIndex];
      const slideType = activeSlide && activeSlide.dataset.slideType;

      if (slideType === 'video_slide') {
        const video = activeSlide && activeSlide.querySelector('video');
        if (video) {
          const playPromise = video.play();
          if (this.lastVideo && this.lastVideo !== video) this.lastVideo.pause();
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                this.lastVideo = video;
              })
              .catch(() => {});
          }
        }
      } else if (this.lastVideo) {
        this.lastVideo.pause();
        this.lastVideo = null;
      }
    }

    _onBlockSelect(event) {
      if (!this.slider || this.slider.destroyed) return;
      const target = event.target;
      if (!target || !this.contains(target)) return;
      const slideIndex = Number(target.dataset.slide);
      if (Number.isNaN(slideIndex)) return;
      this.lastActive = slideIndex;
      if (this.slider.params.loop) {
        this.slider.slideToLoop(slideIndex);
      } else {
        this.slider.slideTo(slideIndex);
      }
    }
  }

  customElements.define('m-slideshow', MSlideshow);
})();
